import { useEffect, useMemo, useState } from "react";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Menu } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { usePortalPersonalNav } from "@/hooks/usePortalPersonalNav";
import {
  PortalPersonalImpersonationProvider,
  usePortalPersonalImpersonation,
} from "@/contexts/PortalPersonalImpersonationContext";
import { PortalPersonalImpersonationSelector } from "./PortalPersonalImpersonationSelector";
import { ImpersonationViewModeBanner } from "@/components/admin/ImpersonationViewModeToggle";
import { usePortal } from "@/lib/portal-personal/portal-store";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";

const PortalPersonalLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { impersonatedUser, isImpersonating } = usePortalPersonalImpersonation();
  const canImpersonate = profile?.puede_impersonar === true;
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const { items, isLoading: cargandoNav } = usePortalPersonalNav();
  const [mobileOpen, setMobileOpen] = useState(false);

  const modoPresentacion = usePortal((s) => s.modo_presentacion);
  const setModoPresentacion = usePortal((s) => s.setModoPresentacion);

  // El nombre/correo que ve el portal sigue al usuario suplantado.
  useEffect(() => {
    usePortal.setState((s) => ({
      usuario: {
        ...s.usuario,
        nombre: isImpersonating
          ? impersonatedUser!.nombre
          : profile?.nombre || profile?.email || s.usuario.nombre,
        correo: isImpersonating
          ? impersonatedUser!.email
          : profile?.email || s.usuario.correo,
        rol: isImpersonating
          ? impersonatedUser!.rol_nombre
          : profile?.rol_nombre || s.usuario.rol,
      },
    }));
  }, [isImpersonating, impersonatedUser, profile?.nombre, profile?.email, profile?.rol_nombre]);

  const isActive = (path: string) =>
    path === "/admin/portal-personal"
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + "/");

  const currentSection = useMemo(
    () => items.find((i) => isActive(i.path))?.label ?? "Portal Personal",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, location.pathname],
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const activeUserName = isImpersonating
    ? impersonatedUser?.nombre || impersonatedUser?.email || "Usuario"
    : profile?.nombre || profile?.email || "Usuario";

  const rawName = profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const userRole = profile?.rol_nombre ?? "Usuario";
  const inits = (s: string) =>
    s.split(" ").filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join("") || "U";
  const fotoVista = useFotoPerfilVista();

  const sidebarContent = (
    <PortalSidebar
      portal="Portal Personal"
      cargando={cargandoNav}
      vacio={
        isImpersonating
          ? `${impersonatedUser?.nombre ?? "Este usuario"} no tiene vistas habilitadas en este portal.`
          : "No tienes vistas habilitadas en este portal."
      }
      groups={[{
        items: items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }]}
      usuario={{ nombre: userName, rol: userRole, iniciales: inits(userName) }}
      onRegresar={canReturnToAdmin ? () => handleNavigate("/admin") : undefined}
      onCerrarSesion={signOut}
    />
  );

  return (
    <div className="flex min-h-screen antialiased">
      <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
        {sidebarContent}
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
          {sidebarContent}
        </SheetContent>
      </Sheet>

      <div className="min-w-0 flex-1 lg:pl-64">
        {isImpersonating && <ImpersonationViewModeBanner targetName={impersonatedUser?.nombre} />}
        <header className="sticky top-0 z-20 hidden h-14 items-center justify-between border-b border-border bg-card px-6 lg:flex">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <span className="font-medium">Portal Personal</span>
            <span className="text-muted-foreground">-</span>
            <span className="text-muted-foreground">{currentSection}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setModoPresentacion(!modoPresentacion)}
              className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/60"
              aria-label="Modo presentación: oculta montos sensibles para presentar en pantalla"
              aria-pressed={modoPresentacion}
            >
              {modoPresentacion ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              Modo presentación
            </button>
            {canImpersonate && <PortalPersonalImpersonationSelector />}
            <div className="flex min-w-0 items-center gap-3">
              <div className="min-w-0 text-right">
                <p className="truncate text-sm font-medium text-foreground">{activeUserName}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {isImpersonating ? impersonatedUser?.rol_nombre : userRole}
                </p>
              </div>
              <AvatarPersona nombre={activeUserName} fotoUrl={fotoVista} size="md" className="size-9" />
            </div>
          </div>
        </header>

        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-card px-3 lg:hidden">
          <div className="flex min-w-0 items-center gap-2">
            <button
              onClick={() => setMobileOpen(true)}
              className="-ml-1 rounded-md p-2 text-foreground hover:bg-muted"
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" />
            </button>
            <p className="truncate text-[15px] font-semibold tracking-tight text-foreground">
              {currentSection}
            </p>
          </div>
          <AvatarPersona nombre={userName} fotoUrl={fotoVista} size="sm" />
        </header>

        <main className="min-h-[calc(100vh-56px)] bg-background px-4 py-5 lg:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const PortalPersonalLayout = () => (
  <PortalPersonalImpersonationProvider>
    <PortalPersonalLayoutInner />
  </PortalPersonalImpersonationProvider>
);

export default PortalPersonalLayout;
